import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, HandCoins, Landmark, Scale, Smartphone, Wallet, type LucideIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FinancialPositionAdjustBalanceDialog } from '@/components/spending/financial-position-adjust-balance-dialog';
import { FinancialPositionRecordTransactionDialog } from '@/components/spending/financial-position-record-transaction-dialog';
import { FinancialPositionTransactionDetailDialog } from '@/components/spending/financial-position-transaction-detail-dialog';
import type { ContributionMethod } from '@/lib/contribution-methods';
import { ChartCard } from '@/components/shared/chart-card';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import {
  DateRangeFilter,
  LIST_FILTER_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { FormField } from '@/components/shared/form-field';
import { MoneyDisplay } from '@/components/shared/money-display';
import { Pagination } from '@/components/shared/pagination';
import { SimpleLineChart } from '@/components/shared/simple-line-chart';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api, type OffsetPage } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate, formatDateTime, formatMoney } from '@/utils/format';

type PositionKind =
  | 'bank_account'
  | 'cash'
  | 'mobile_money'
  | 'hubtel_account'
  | 'fixed_asset'
  | 'liability';

const KIND_ICONS: Record<PositionKind, LucideIcon> = {
  bank_account: Landmark,
  cash: Wallet,
  mobile_money: Smartphone,
  hubtel_account: HandCoins,
  fixed_asset: Building2,
  liability: Scale,
};

export interface FinancialPositionItem {
  id: string;
  kind: PositionKind;
  name: string;
  balanceMinor: number;
  currency: string;
  balanceAsOfDate: string;
  isActive: boolean;
}

interface AccountTransaction {
  id: string;
  itemId: string;
  direction: 'in' | 'out';
  method: ContributionMethod;
  partyKind: 'member' | 'company' | 'other';
  partyName: string;
  amountMinor: number;
  currency: string;
  transactionDate: string;
  reference: string | null;
  note: string | null;
  funds?: Array<{ fundId: string | null; fundName: string | null; amountMinor: number }>;
  isVoided: boolean;
  voidReason: string | null;
  createdAt: string;
}

interface BalanceAdjustment {
  id: string;
  itemId: string;
  previousBalanceMinor: number;
  newBalanceMinor: number;
  balanceAsOfDate: string;
  currency: string;
  reason: string;
  adjustedByUserId: string;
  adjustedByName: string;
  createdAt: string;
}

export interface FinancialPositionDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: FinancialPositionItem | null;
  canManage: boolean;
}

const adjustmentQueryParams = (dateRange: DateRange, page: number, pageSize: number) => ({
  page,
  pageSize,
  dateFrom: dateRange.from ?? undefined,
  dateTo: dateRange.to ?? undefined,
});

export const FinancialPositionDetailDialog = ({
  open,
  onOpenChange,
  item,
  canManage,
}: FinancialPositionDetailDialogProps) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const timezone = usePreferences((state) => state.timezone);
  const locale = usePreferences((state) => state.locale);

  const [editNameOpen, setEditNameOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [detailTransaction, setDetailTransaction] = useState<AccountTransaction | null>(null);
  const [voidTarget, setVoidTarget] = useState<AccountTransaction | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidPassword, setVoidPassword] = useState('');
  const [transactionPage, setTransactionPage] = useState(1);
  const [historyTab, setHistoryTab] = useState<'transactions' | 'adjustments'>('transactions');
  const [editName, setEditName] = useState('');
  const [adjustmentPage, setAdjustmentPage] = useState(1);
  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    preset: 'allTime',
    ...resolvePreset('allTime', timezone),
  }));

  const balanceAdjustments = useQuery({
    queryKey: [
      'financial-position',
      'items',
      item?.id,
      'balance-adjustments',
      adjustmentPage,
      dateRange.from,
      dateRange.to,
    ],
    queryFn: () =>
      api.get<{ data: BalanceAdjustment[]; page: OffsetPage }>(
        `/financial-position/items/${item!.id}/balance-adjustments`,
        { query: adjustmentQueryParams(dateRange, adjustmentPage, 20) },
      ),
    enabled: open && Boolean(item),
    placeholderData: keepPreviousData,
  });

  const transactions = useQuery({
    queryKey: [
      'financial-position',
      'items',
      item?.id,
      'transactions',
      transactionPage,
      dateRange.from,
      dateRange.to,
    ],
    queryFn: () =>
      api.get<{ data: AccountTransaction[]; page: OffsetPage }>(
        `/financial-position/items/${item!.id}/transactions`,
        { query: adjustmentQueryParams(dateRange, transactionPage, 20) },
      ),
    enabled: open && Boolean(item),
    placeholderData: keepPreviousData,
  });

  const chartTransactions = useQuery({
    queryKey: [
      'financial-position',
      'items',
      item?.id,
      'transactions',
      'chart',
      dateRange.from,
      dateRange.to,
    ],
    queryFn: () =>
      api.get<{ data: AccountTransaction[]; page: OffsetPage }>(
        `/financial-position/items/${item!.id}/transactions`,
        { query: adjustmentQueryParams(dateRange, 1, 100) },
      ),
    enabled: open && Boolean(item),
    placeholderData: keepPreviousData,
  });

  const chartAdjustments = useQuery({
    queryKey: [
      'financial-position',
      'items',
      item?.id,
      'balance-adjustments',
      'chart',
      dateRange.from,
      dateRange.to,
    ],
    queryFn: () =>
      api.get<{ data: BalanceAdjustment[]; page: OffsetPage }>(
        `/financial-position/items/${item!.id}/balance-adjustments`,
        { query: adjustmentQueryParams(dateRange, 1, 100) },
      ),
    enabled: open && Boolean(item),
    placeholderData: keepPreviousData,
  });

  const adjustmentColumns: DataTableColumn<BalanceAdjustment>[] = useMemo(
    () => [
      {
        id: 'date',
        header: t('financialPosition.adjustmentColumns.date'),
        cell: (row) => formatDateTime(row.createdAt),
      },
      {
        id: 'asOf',
        header: t('financialPosition.columns.asOf'),
        cell: (row) => formatDate(row.balanceAsOfDate),
      },
      {
        id: 'previousBalance',
        header: t('financialPosition.adjustmentColumns.previousBalance'),
        money: (row) => ({ amount: row.previousBalanceMinor, currency: row.currency }),
      },
      {
        id: 'newBalance',
        header: t('financialPosition.adjustmentColumns.newBalance'),
        money: (row) => ({ amount: row.newBalanceMinor, currency: row.currency }),
      },
      {
        id: 'adjustedBy',
        header: t('financialPosition.adjustmentColumns.adjustedBy'),
        cell: (row) => row.adjustedByName || '—',
      },
      {
        id: 'reason',
        header: t('financialPosition.adjustmentColumns.reason'),
        cell: (row) => row.reason,
        hideBelowMd: true,
      },
    ],
    [t],
  );

  const transactionColumns: DataTableColumn<AccountTransaction>[] = useMemo(
    () => [
      {
        id: 'date',
        header: t('financialPosition.transactions.columns.date'),
        cell: (row) => formatDate(row.transactionDate),
      },
      {
        id: 'direction',
        header: t('financialPosition.transactions.columns.direction'),
        cell: (row) => t(`financialPosition.transactions.directions.${row.direction}`),
      },
      {
        id: 'method',
        header: t('financialPosition.transactions.columns.method'),
        cell: (row) => t(`expenses.methods.${row.method}`),
        hideBelowMd: true,
      },
      {
        id: 'party',
        header: t('financialPosition.transactions.columns.party'),
        cell: (row) => row.partyName,
      },
      {
        id: 'funds',
        header: t('financialPosition.transactions.columns.funds'),
        cell: (row) =>
          row.funds && row.funds.length > 0
            ? row.funds
                .map(
                  (line) =>
                    `${line.fundName ?? t('financialPosition.transactions.fundSplit.notTied')} ${formatMoney(line.amountMinor, row.currency)}`,
                )
                .join(', ')
            : '—',
        hideBelowMd: true,
      },
      {
        id: 'amount',
        header: t('financialPosition.transactions.columns.amount'),
        money: (row) => ({
          amount: row.direction === 'out' ? -row.amountMinor : row.amountMinor,
          currency: row.currency,
          parenthesiseNegative: true,
          colorBySign: true,
        }),
      },
      {
        id: 'reference',
        header: t('financialPosition.transactions.columns.reference'),
        cell: (row) => row.reference || '—',
        hideBelowMd: true,
      },
    ],
    [t],
  );

  const chartData = useMemo(() => {
    if (!item) return [];
    const events: Array<{ at: number; label: string; apply: (balance: number) => number }> = [];
    for (const row of chartAdjustments.data?.data ?? []) {
      events.push({
        at: Date.parse(row.createdAt),
        label: formatDateTime(row.createdAt, { locale }),
        apply: () => row.newBalanceMinor,
      });
    }
    for (const row of chartTransactions.data?.data ?? []) {
      if (row.isVoided) continue;
      const delta = row.direction === 'out' ? -row.amountMinor : row.amountMinor;
      events.push({
        at: Date.parse(row.createdAt),
        label: formatDate(row.transactionDate, { locale }),
        apply: (balance) => balance + delta,
      });
    }
    events.sort((left, right) => left.at - right.at);
    let balance = 0;
    return events.map((event) => {
      balance = event.apply(balance);
      return { label: event.label, value: balance };
    });
  }, [chartAdjustments.data?.data, chartTransactions.data?.data, item, locale]);

  const openEditName = () => {
    if (!item) return;
    setEditName(item.name);
    setEditNameOpen(true);
  };

  const updateName = useMutation({
    mutationFn: () =>
      api.patch<FinancialPositionItem>(`/financial-position/items/${item!.id}`, {
        name: editName.trim(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['financial-position'] });
      await queryClient.invalidateQueries({ queryKey: ['reports', 'run', 'balance_sheet'] });
      setEditNameOpen(false);
      toast.success(t('financialPosition.saved'));
    },
    onError: (error) => {
      if (error instanceof ApiError) toast.error(tErrors(error.code, error.params));
      else toast.error(tCommon('toast.somethingWentWrong'));
    },
  });

  const voidTransaction = useMutation({
    mutationFn: () =>
      api.post(`/financial-position/items/${item!.id}/transactions/${voidTarget!.id}/void`, {
        reason: voidReason.trim(),
        password: voidPassword,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['financial-position'] });
      await queryClient.invalidateQueries({ queryKey: ['reports', 'run', 'balance_sheet'] });
      setVoidTarget(null);
      setVoidReason('');
      setVoidPassword('');
      toast.success(t('financialPosition.transactions.voided'));
    },
    onError: (error) => {
      if (error instanceof ApiError) toast.error(tErrors(error.code, error.params));
      else toast.error(tCommon('toast.somethingWentWrong'));
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setAdjustmentPage(1);
      setTransactionPage(1);
      setHistoryTab('transactions');
      setEditNameOpen(false);
      setAdjustOpen(false);
      setRecordOpen(false);
      setVoidTarget(null);
      setVoidReason('');
      setVoidPassword('');
      setDateRange({
        preset: 'allTime',
        ...resolvePreset('allTime', timezone),
      });
    }
    onOpenChange(nextOpen);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    setAdjustmentPage(1);
    setTransactionPage(1);
  };

  const adjustmentPageMeta = balanceAdjustments.data?.page;
  const transactionPageMeta = transactions.data?.page;
  const KindIcon = item ? KIND_ICONS[item.kind] : null;
  const dateFilterLabels = {
    today: tCommon('date.today'),
    yesterday: tCommon('date.yesterday'),
    thisWeek: tCommon('date.thisWeek'),
    lastWeek: tCommon('date.lastWeek'),
    thisMonth: tCommon('date.thisMonth'),
    lastMonth: tCommon('date.lastMonth'),
    thisQuarter: tCommon('date.thisQuarter'),
    thisYear: tCommon('date.thisYear'),
    lastYear: tCommon('date.lastYear'),
    allTime: tCommon('date.allTime'),
    custom: tCommon('date.custom'),
    from: t('financialPosition.detail.dateFrom'),
    to: t('financialPosition.detail.dateTo'),
  };

  return (
    <>
      <Dialog open={open && Boolean(item)} onOpenChange={handleOpenChange}>
        <DialogContent size="2xl" closeLabel={tCommon('action.close')} className="!max-h-[90dvh]">
          <DialogHeader className="!pr-12">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <DialogTitle>{item?.name}</DialogTitle>
                {item ? (
                  <p className="mt-1 text-body-sm text-fg-secondary">
                    {t(`financialPosition.kind.${item.kind}`)}
                  </p>
                ) : null}
              </div>
              {canManage && item ? (
                <div className="flex flex-wrap justify-end gap-2">
                  <Button variant="outline" onClick={openEditName}>
                    {t('financialPosition.editName')}
                  </Button>
                  <Button variant="outline" onClick={() => setAdjustOpen(true)}>
                    {t('financialPosition.adjustBalance.action')}
                  </Button>
                  <Button onClick={() => setRecordOpen(true)}>
                    {t('financialPosition.transactions.record')}
                  </Button>
                </div>
              ) : null}
            </div>
          </DialogHeader>

          {item ? (
            <DialogBody
              className="!max-h-[calc(90dvh-11rem)]"
              contentClassName="flex flex-col gap-6"
            >
              <div className="grid gap-4 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:items-stretch">
                <Card className="border-brand/15 bg-brand/[0.04] p-5 shadow-e1">
                  <div className="flex h-full items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-body-sm font-medium text-fg-secondary">
                        {t('financialPosition.balance')}
                      </p>
                      <div className="mt-2">
                        <MoneyDisplay
                          amount={item.balanceMinor}
                          currency={item.currency}
                          size="xl"
                          parenthesiseNegative={item.kind === 'liability'}
                        />
                      </div>
                      <p className="mt-2 text-body-sm text-fg-tertiary">
                        {t('financialPosition.asOfLabel', {
                          date: formatDate(item.balanceAsOfDate),
                        })}
                      </p>
                    </div>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
                      {KindIcon ? <KindIcon className="size-5" aria-hidden /> : null}
                    </span>
                  </div>
                </Card>

                <ChartCard
                  title={t('financialPosition.detail.balanceTrend')}
                  loading={chartAdjustments.isLoading || chartTransactions.isLoading}
                  hasData={chartData.length > 0}
                  height={168}
                  empty={{
                    title: t('financialPosition.detail.trendEmpty.title'),
                    body:
                      dateRange.preset === 'allTime' && !dateRange.from && !dateRange.to
                        ? t('financialPosition.detail.trendEmpty.body')
                        : t('financialPosition.detail.trendEmptyFiltered'),
                  }}
                  className="min-w-0"
                >
                  <SimpleLineChart
                    data={chartData}
                    height={168}
                    formatValue={(value) =>
                      formatMoney(value, item.currency, {
                        locale,
                        parenthesiseNegative: item.kind === 'liability',
                      })
                    }
                  />
                </ChartCard>
              </div>

              <Tabs value={historyTab} onValueChange={(value) => setHistoryTab(value as 'transactions' | 'adjustments')}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <TabsList>
                    <TabsTrigger value="transactions">
                      {t('financialPosition.transactions.title')}
                    </TabsTrigger>
                    <TabsTrigger value="adjustments">
                      {t('financialPosition.adjustmentHistory')}
                    </TabsTrigger>
                  </TabsList>
                  <DateRangeFilter
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    popoverPortal="dialog"
                    presets={LIST_FILTER_DATE_RANGE_PRESETS}
                    labels={dateFilterLabels}
                    className="sm:max-w-none"
                  />
                </div>

              <TabsContent value="transactions" className="mt-4 min-w-0 space-y-3">
                <DataTable
                  rows={transactions.data?.data ?? []}
                  columns={transactionColumns}
                  rowKey={(row) => row.id}
                  loading={transactions.isLoading}
                  onRowClick={setDetailTransaction}
                  empty={
                    <p className="text-body-sm text-fg-secondary">
                      {dateRange.preset === 'allTime' && !dateRange.from && !dateRange.to
                        ? t('financialPosition.transactions.empty')
                        : t('financialPosition.transactions.emptyFiltered')}
                    </p>
                  }
                  actionsHeader={tCommon('table.actions')}
                  rowActions={
                    canManage
                      ? (row) =>
                          row.isVoided ? (
                            <span className="text-body-sm text-fg-secondary">
                              {t('financialPosition.transactions.voidedLabel')}
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setVoidTarget(row);
                                setVoidReason('');
                                setVoidPassword('');
                              }}
                            >
                              {t('financialPosition.transactions.void')}
                            </Button>
                          )
                      : undefined
                  }
                />
                {transactionPageMeta && transactionPageMeta.totalPages > 1 ? (
                  <Pagination
                    page={transactionPageMeta.page}
                    pageSize={transactionPageMeta.pageSize}
                    totalItems={transactionPageMeta.totalItems}
                    totalPages={transactionPageMeta.totalPages}
                    onPageChange={setTransactionPage}
                    labels={{
                      showing: ({ from, to, total }) =>
                        tCommon('pagination.showing', { from, to, total }),
                      perPage: tCommon('pagination.perPage'),
                      first: tCommon('pagination.first'),
                      previous: tCommon('pagination.previous'),
                      next: tCommon('pagination.next'),
                      last: tCommon('pagination.last'),
                    }}
                  />
                ) : null}
              </TabsContent>

              <TabsContent value="adjustments" className="mt-4 min-w-0 space-y-3">
                <DataTable
                  rows={balanceAdjustments.data?.data ?? []}
                  columns={adjustmentColumns}
                  rowKey={(row) => row.id}
                  loading={balanceAdjustments.isLoading}
                  empty={
                    <p className="text-body-sm text-fg-secondary">
                      {dateRange.preset === 'allTime' && !dateRange.from && !dateRange.to
                        ? t('financialPosition.adjustmentsEmpty')
                        : t('financialPosition.detail.adjustmentsEmptyFiltered')}
                    </p>
                  }
                />
                {adjustmentPageMeta && adjustmentPageMeta.totalPages > 1 ? (
                  <Pagination
                    page={adjustmentPageMeta.page}
                    pageSize={adjustmentPageMeta.pageSize}
                    totalItems={adjustmentPageMeta.totalItems}
                    totalPages={adjustmentPageMeta.totalPages}
                    onPageChange={setAdjustmentPage}
                    labels={{
                      showing: ({ from, to, total }) =>
                        tCommon('pagination.showing', { from, to, total }),
                      perPage: tCommon('pagination.perPage'),
                      first: tCommon('pagination.first'),
                      previous: tCommon('pagination.previous'),
                      next: tCommon('pagination.next'),
                      last: tCommon('pagination.last'),
                    }}
                  />
                ) : null}
              </TabsContent>
              </Tabs>
            </DialogBody>
          ) : null}

          <DialogFooter className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => handleOpenChange(false)}>
              {tCommon('action.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editNameOpen} onOpenChange={setEditNameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('financialPosition.editName')}</DialogTitle>
          </DialogHeader>
          <DialogBody contentClassName="flex flex-col gap-4">
            <FormField label={t('financialPosition.name')} required>
              {() => (
                <Input
                  value={editName}
                  onChange={(event) => setEditName(event.target.value)}
                />
              )}
            </FormField>
          </DialogBody>
          <DialogFooter className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditNameOpen(false)}>
              {tCommon('action.cancel')}
            </Button>
            <Button
              onClick={() => updateName.mutate()}
              disabled={!editName.trim() || updateName.isPending}
            >
              {tCommon('action.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FinancialPositionTransactionDetailDialog
        open={Boolean(detailTransaction)}
        onOpenChange={(next) => {
          if (!next) setDetailTransaction(null);
        }}
        transaction={detailTransaction}
      />

      {canManage && item ? (
        <FinancialPositionRecordTransactionDialog
          open={recordOpen}
          onOpenChange={setRecordOpen}
          itemId={item.id}
          itemName={item.name}
          currency={item.currency}
        />
      ) : null}

      <Dialog
        open={Boolean(voidTarget)}
        onOpenChange={(next) => {
          if (!next) {
            setVoidTarget(null);
            setVoidPassword('');
          }
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('financialPosition.transactions.voidTitle')}</DialogTitle>
          </DialogHeader>
          <DialogBody contentClassName="flex flex-col gap-4">
            {voidTarget && item ? (
              <p className="text-body-sm text-fg-secondary">
                {t('financialPosition.transactions.voidDescription', {
                  amount: formatMoney(
                    voidTarget.direction === 'out' ? -voidTarget.amountMinor : voidTarget.amountMinor,
                    voidTarget.currency,
                    { locale, parenthesiseNegative: true },
                  ),
                  name: item.name,
                })}
              </p>
            ) : null}
            <FormField label={t('financialPosition.transactions.voidReason')} required>
              {(field) => (
                <Textarea
                  id={field.id}
                  value={voidReason}
                  onChange={(event) => setVoidReason(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder={t('financialPosition.transactions.voidReasonPlaceholder')}
                />
              )}
            </FormField>
            <FormField
              label={t('financialPosition.transactions.voidPassword')}
              hint={t('financialPosition.transactions.voidPasswordHint')}
              required
            >
              {(field) => (
                <Input
                  id={field.id}
                  type="password"
                  value={voidPassword}
                  onChange={(event) => setVoidPassword(event.target.value)}
                  autoComplete="current-password"
                  disabled={voidTransaction.isPending}
                />
              )}
            </FormField>
          </DialogBody>
          <DialogFooter className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setVoidTarget(null)}>
              {tCommon('action.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={voidReason.trim().length < 3 || voidPassword.length === 0}
              loading={voidTransaction.isPending}
              onClick={() => voidTransaction.mutate()}
            >
              {t('financialPosition.transactions.voidConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {canManage && item ? (
        <FinancialPositionAdjustBalanceDialog
          open={adjustOpen}
          onOpenChange={setAdjustOpen}
          itemId={item.id}
          itemName={item.name}
          currency={item.currency}
          currentBalanceMinor={item.balanceMinor}
          currentBalanceAsOfDate={item.balanceAsOfDate}
        />
      ) : null}
    </>
  );
};
